import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import {AttrsSchema, Component} from 'panel';

import {emitTrackEvent} from 'common/util/dom';
import {filterAttributes} from 'common/util';
import {pluralize} from 'common/util/string';
import {apiClient} from 'common/util/api-client';
import {currentProjectStore} from 'common/report/stores/project-details';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {getProjectId, getReportUrl, getWorkspaceId} from 'common/report/util';
import {getEntityTypePermissions, isSharingEnabledForProject} from 'common/report/util/saved-entities';
import {EntityType, SharableEntityType} from 'common/report/util/saved-entities/types';
import {promptSharing} from 'common/widgets/sharing';
import {promisifyModal} from 'common/components/modal/utils';
import {Visibility} from 'common/components/base/stackable';
import {customEventsStore} from 'common/report/stores/custom-events';
import {useStore} from 'common/util/component-helpers';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {ResourceType} from 'common/report/constants';

import {DisplayContext as PropertyFilterDisplayContext} from '../property-filter-menu/models/types';
import template from './index.jade';
import css from './index.styl?inline';
import {EventSelector, MPCustomEvent} from './models/events';
import {SerializedCustomEvent} from './models/types';

const MAX_NAME_LENGTH = 255;
const DISPATCHED_EVENTS = {
  MODAL_CLOSED: `modalClosed`,
  DELETED: `deletedCustomEvent`,
  DELETING: `deletingCustomEvent`,
  SAVING: `savingCustomEvent`,
  SAVED: `savedCustomEvent`,
};

export async function promptCustomEvents(
  attrs,
): Promise<{appliedValue: SerializedCustomEvent; didClose: boolean; deletedId: string | number}> {
  const closeEv = await promisifyModal({
    tagName: `mp-custom-event-modal`,
    attrs,
    closeEvents: [`savedCustomEvent`, `deletedCustomEvent`, `modalClosed`],
  });

  return {
    appliedValue: closeEv.type === `savedCustomEvent` ? closeEv.detail?.customEvent : null,
    didClose:
      closeEv.type === `modalClosed` || (closeEv.type === `change` && closeEv.detail.state === Visibility.Closed),
    deletedId: closeEv.type === `deletedCustomEvent` ? closeEv.detail.id : null,
  };
}

interface CustomEventModalState {
  error: Obj;
  open: boolean;
  showDeleteModal: boolean;
  stagedCustomEvent: MPCustomEvent;
  affectedMessageIds: number[];
  message: string;
}

interface CustomEventModalAttrs {
  'is-blocked-people': boolean;
  open: boolean;
  'project-has-people': boolean;
  'project-id': number;
  'selected-custom-event-id': number;
  'workspace-id': number;
  'all-data-groups': boolean;
  'project-as-of-date': string;
  'track-name': string;
}

export class CustomEventModalInternal extends Component<CustomEventModalState, CustomEventModalAttrs> {
  private debouncedGetAffectedMessages: any;
  nameInputEl: any;
  initialCustomEvent: MPCustomEvent;

  static get attrsSchema(): AttrsSchema<CustomEventModalAttrs> {
    return {
      ...super.attrsSchema,
      'is-blocked-people': `boolean`,
      open: `boolean`,
      'project-has-people': `boolean`,
      'project-id': `string`,
      'selected-custom-event-id': `number`,
      'workspace-id': `number`,
      'all-data-groups': `boolean`,
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
      'track-name': `string`,
    };
  }
  constructor() {
    super();
    this.debouncedGetAffectedMessages = debounce(this.getAffectedMessages, 50);
  }

  static get dispatchedEvents() {
    return Object.values(DISPATCHED_EVENTS);
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    switch (attr) {
      case `selected-custom-event-id`:
        if (this.attr(attr)) {
          this.updateStagedCustomEvent();
        } else {
          this.clearStagedCustomEvent();
        }
        break;
      case `open`: {
        const open = this.attr(attr);
        this.update({open});
        break;
      }
      default:
        break;
    }
    if (
      this.initialized &&
      this.attr(`selected-custom-event-id`) &&
      [`workspace-id`, `project-id`, `selected-custom-event-id`].includes(attr)
    ) {
      this.debouncedGetAffectedMessages(this.attr(`selected-custom-event-id`));
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.initialCustomEvent = new MPCustomEvent({...this.state.stagedCustomEvent});

    useStore(this, currentProjectStore).fetch();
    useStore(this, customEventsStore).fetch({projectId: getProjectId(), workspaceId: getWorkspaceId()});
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        [this.builderStateReference]: this.defaultBuilderState,
        error: {},
        open: false,
        showDeleteModal: false,
        stagedCustomEvent: new MPCustomEvent(),
        affectedMessageIds: [],
        message: ``,
      },
      helpers: {
        MAX_NAME_LENGTH,
        PropertyFilterDisplayContext,
        pick,
        pluralize,
        filterAttributes,
        getSharingBannerAttrs: () => ({
          'project-id': this.attr(`project-id`),
          'entity-type': EntityType.CustomEvent,
        }),
        showShareManagement: () => {
          if (isSharingEnabledForProject(currentProjectStore.value)) {
            const entityTypePermissions = getEntityTypePermissions(this.attr(`project-id`), EntityType.CustomEvent);
            return entityTypePermissions.can_share;
          }
          return false;
        },
        handleModalChange: ({detail}) => {
          if (detail && detail.state === `closed`) {
            this.update({open: false});
            requestAnimationFrame(() => {
              this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.MODAL_CLOSED));
              // clear errors and other transient state when modal closes
              this.update(pick(this.config.defaultState, [`showDeleteModal`, `error`]));
            });
          }
        },

        handleErrorToastClosed: () => {
          this.update({error: {}});
        },

        hasUnsavedChanges: () => {
          return !isEqual(this.initialCustomEvent, this.state.stagedCustomEvent);
        },

        getEntries: () => {
          const {stagedCustomEvent} = this.state;
          return stagedCustomEvent.eventSelectors.map((eventSelector) => ({
            event: {
              custom: eventSelector.eventIsCustom(),
              label: eventSelector.getEventLabel(),
              value: eventSelector.getEventName(),
              ...(eventSelector.eventIsCustom()
                ? {
                    id: eventSelector.getEventId(),
                  }
                : {}),
            },
            filters: eventSelector.propertyFilters.map((propertyFilter) => propertyFilter.getAttributes()),
            filtersOperator: eventSelector.filtersOperator,
            type: ResourceType.Event,
          }));
        },

        updateQueryEvents: (ev) => {
          const {stagedCustomEvent} = this.state;
          stagedCustomEvent.eventSelectors = ev.detail.entries.map(
            ({filters, event, filtersOperator}) =>
              new EventSelector({
                propertyFilters: filters,
                event: event.custom ? {name: event.label, id: event.id} : event.value,
                filtersOperator,
              }),
          );
          this.update({stagedCustomEvent});
        },

        captureTracking: (ev) => {
          const {eventName, props} = ev.detail;
          ev.stopPropagation();
          this.track(eventName, props || {});
        },
        isNewCustomEvent: () => !this.state.stagedCustomEvent.id,
        isStagedCustomEventValid: () => {
          const {stagedCustomEvent, error} = this.state;
          return stagedCustomEvent.isValid() && !Object.values(error).find(Boolean);
        },
        clearNameErrors: () => this.update({error: {}}),
        clickSave: ({andShare = false}) => {
          if (this.helpers.isStagedCustomEventValid()) {
            this.saveChanges({andShare});
          }
        },
        nameInputInserted: ({elm}) => {
          this.nameInputEl = elm;
        },
        updateCustomEventName: (ev) => {
          const stagedCustomEvent = this.state.stagedCustomEvent.updateName(ev.target.value.trim());
          this.update({stagedCustomEvent});
          this.validateStagedCustomEvent();
        },
        clickDeleteCustomEvent: () => {
          this.update({showDeleteModal: true});
        },
        confirmDeleteCustomEvent: () => {
          this.deleteCustomEvent(this.state.stagedCustomEvent.id);
        },
        cancelDeleteCustomEvent: () => {
          this.update({showDeleteModal: false});
        },
      },
    };
  }

  validateStagedCustomEvent() {
    const {name, id} = this.state.stagedCustomEvent;
    const error = {
      invalidName: !name,
      duplicateName: false,
    };

    if (name) {
      const otherCustomEvents = this.customEvents.filter((customEvent) => customEvent.id !== id);
      error.duplicateName = !!otherCustomEvents.find(
        (customEvent) => customEvent.name.toLowerCase() === name.toLowerCase(),
      );
    }
    this.update({error});
  }

  async deleteCustomEvent(id) {
    const {affectedMessageIds} = this.state;
    this.track(`Delete custom event`);
    this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.DELETING));
    try {
      if (!isEmpty(affectedMessageIds)) {
        await this.pauseMessages(affectedMessageIds);
      }

      await customEventsStore.delete(id, {
        customEventId: id,
        projectId: getProjectId(),
        workspaceId: getWorkspaceId(),
      });

      this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.DELETED, {detail: {id}}));
    } catch (err) {
      this.update({error: {deleteCustomEvent: true}, showDeleteModal: false, message: err.message});
      console.error(err);
    }
  }

  async saveChanges({andShare = false}) {
    const {stagedCustomEvent, affectedMessageIds} = this.state;
    const trackingEvent = stagedCustomEvent.id ? `Update custom event` : `Save new custom event`;
    this.track(trackingEvent);
    this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.SAVING));
    try {
      if (!isEmpty(affectedMessageIds)) {
        await this.pauseMessages(affectedMessageIds);
      }

      const result = await customEventsStore.post({
        customEventId: stagedCustomEvent.id,
        customEvent: stagedCustomEvent.toSerializedCustomEvent(),
        projectId: getProjectId(),
        workspaceId: getWorkspaceId(),
      });

      // force event definitions to reload
      eventDefinitionsStore.refresh({projectId: getProjectId(), workspaceId: getWorkspaceId()});

      this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.SAVED, {detail: {customEvent: result}}));
      this.clearStagedCustomEvent();
      if (andShare) {
        await promptSharing({
          type: SharableEntityType.CustomEvent,
          id: result.id,
          'project-id': this.attr(`project-id`),
        });
      }
    } catch (err) {
      this.update({error: {saveCustomEvent: true, message: err.message}});
      console.warn(err);
    }
  }

  updateStagedCustomEvent() {
    const customEventId = this.attr(`selected-custom-event-id`);
    if (customEventId) {
      this.update({stagedCustomEvent: MPCustomEvent.fromSerializedCustomEvent(customEventsStore.value[customEventId])});
    }
  }

  clearStagedCustomEvent() {
    this.update({stagedCustomEvent: new MPCustomEvent(), affectedMessageIds: []});
  }

  get customEvents() {
    return Object.values(customEventsStore.value);
  }

  track(eventName, props = {}) {
    emitTrackEvent(this, {
      eventName,
      eventPrefix: `mp-custom-event-modal`,
      props: {
        ...props,
        ...this.state.stagedCustomEvent.toTrackingData(),
      },
    });
  }

  //#region Builder methods

  get builderStateReference() {
    return `builder`;
  }

  get defaultBuilderState() {
    return {
      activeIndex: 0,
      panes: [],
      propertyFilterIndex: null,
      searchFilter: ``,
      selectorIndex: null,
    };
  }

  //#endregion

  //#region Notifications methods
  async pauseMessages(messageIds) {
    const pauseUrl = getReportUrl(`triggers/pause/`, {
      projectId: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
    });
    await apiClient.post(pauseUrl, {
      trigger_ids: messageIds,
    });
  }

  getAffectedMessages(customEventId) {
    const workspaceId = this.attr(`workspace-id`);
    const workspaceIdOrProjectId = workspaceId ? `workspaces/${workspaceId}` : `projects/${this.attr(`project-id`)}`;
    // using apiClient.fetch on legacy endpoint with incorrect format
    // eslint-disable-next-line local-rules/deprecated-member-access
    return apiClient
      .fetch(`/api/app/${workspaceIdOrProjectId}/custom_events/affected/${customEventId}/`)
      .then((response) => response.json())
      .then((response) => response.notif_ids_to_pause)
      .catch(() => []) // swallow errors
      .then((affectedMessageIds) => {
        this.update({affectedMessageIds});
      });
  }
  //#endregion
}

export const CustomEventModalInternalEl = defineAndMakeJsxFactory(`mp-custom-event-modal`, CustomEventModalInternal);
